import type { MenuProduct } from "@/mocks/branch/menu";
import ToggleSwitch from "./ToggleSwitch";

interface ProductRowProps {
  product: MenuProduct;
  canEditAvailability: boolean;
  canEditQuantities: boolean;
  onToggleAvailable: (id: string) => void;
  onSetQuantity: (id: string, qty: number | null) => void;
  onMarkOutOfStock: (id: string) => void;
}

export default function ProductRow({
  product,
  canEditAvailability,
  canEditQuantities,
  onToggleAvailable,
  onSetQuantity,
  onMarkOutOfStock,
}: ProductRowProps) {
  const isOffered = product.available !== false;
  const isSoldOut = product.quantity === 0;
  const effectiveOn = isOffered && !isSoldOut;

  let statusLabel = "متوفر";
  let statusClass = "text-primary-600";
  if (!isOffered) {
    statusLabel = "غير متوفر";
    statusClass = "text-foreground-400";
  } else if (isSoldOut) {
    statusLabel = "نفد";
    statusClass = "text-red-600";
  }

  const handleQuantityChange = (value: string) => {
    if (value === "") {
      onSetQuantity(product.id, null);
      return;
    }
    const n = parseInt(value, 10);
    if (Number.isNaN(n) || n < 0) return;
    onSetQuantity(product.id, n);
  };

  const step = (delta: number) => {
    const base = product.quantity ?? 0;
    onSetQuantity(product.id, Math.max(0, base + delta));
  };

  return (
    <div
      className={`flex flex-col lg:flex-row lg:items-center gap-3 px-4 py-3 rounded-btn border transition-colors ${
        effectiveOn
          ? "border-background-200 bg-background-50"
          : "border-background-200 bg-background-100"
      }`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-12 h-12 rounded-btn overflow-hidden shrink-0 bg-background-100">
          <img
            src={product.image}
            alt={product.name}
            title={`${product.name} — Your Cashier`}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground-900 truncate">{product.name}</p>
          <p className="text-xs text-foreground-500 font-semibold mt-0.5">
            {product.price.toFixed(2)} ر.س
          </p>
        </div>
      </div>

      {canEditQuantities ? (
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-foreground-400 font-medium whitespace-nowrap">الكمية</span>
          <div className="flex items-center border border-background-300 rounded-btn overflow-hidden">
            <button
              type="button"
              onClick={() => step(-1)}
              className="w-10 h-10 flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-subtract-line text-lg"></i>
            </button>
            <input
              type="text"
              inputMode="numeric"
              value={product.quantity ?? ""}
              onChange={(e) => handleQuantityChange(e.target.value)}
              placeholder="غير محدود"
              className="w-16 h-10 text-center text-sm font-semibold text-foreground-900 outline-none border-x border-background-200 bg-background-50 placeholder:text-foreground-400 placeholder:font-normal"
            />
            <button
              type="button"
              onClick={() => step(1)}
              className="w-10 h-10 flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-add-line text-lg"></i>
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex items-center gap-3 shrink-0">
        <div className="flex items-center gap-2.5">
          {canEditAvailability ? (
            <ToggleSwitch
              checked={isOffered}
              onChange={() => onToggleAvailable(product.id)}
              label={`توفر ${product.name}`}
            />
          ) : null}
          <span className={`text-xs font-bold whitespace-nowrap ${statusClass}`}>
            {statusLabel}
          </span>
        </div>
        {canEditAvailability ? (
          <button
            type="button"
            onClick={() => onMarkOutOfStock(product.id)}
            disabled={!isOffered}
            className="h-10 px-3.5 rounded-btn text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
          >
            نفد
          </button>
        ) : null}
      </div>
    </div>
  );
}